import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL, PAYMENT_LABELS } from '../constants';
import { formatCurrency } from '../utils/formatters';

export const usePayment = (loadOrders, loadCustomers, customers) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [paymentCustomer, setPaymentCustomer] = useState(null);
  const [paymentCustomerSearch, setPaymentCustomerSearch] = useState('');
  const [paymentCustomerResults, setPaymentCustomerResults] = useState([]);
  const [showNewCustomerInPayment, setShowNewCustomerInPayment] = useState(false);
  const [newPaymentCustomer, setNewPaymentCustomer] = useState({ name: '', phone: '', notes: '' });
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Change handling states
  const [changeReturned, setChangeReturned] = useState(true);
  const [saveBalanceAmount, setSaveBalanceAmount] = useState('');

  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  // Points handling states
  const [pointSettings, setPointSettings] = useState(null);
  const [usePoints, setUsePoints] = useState(0);
  const [maxRedeemablePoints, setMaxRedeemablePoints] = useState(0);
  const [pointsDiscount, setPointsDiscount] = useState(0);
  const [pointsToEarn, setPointsToEarn] = useState(0);

  // Load point settings and bank accounts on mount
  useEffect(() => {
    loadPointSettings();
    loadBankAccounts();
  }, []);

  const loadPointSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/points/settings`);
      if (response.data.success) {
        setPointSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading point settings:', error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data?.data?.bankAccounts) {
        setBankAccounts(response.data.data.bankAccounts);
        // Set default bank account
        const defaultBank = response.data.data.bankAccounts.find(b => b.isDefault);
        if (defaultBank) {
          setSelectedBankAccount(defaultBank);
        } else if (response.data.data.bankAccounts.length > 0) {
          setSelectedBankAccount(response.data.data.bankAccounts[0]);
        }
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  // Calculate max redeemable points when customer or order changes
  const calculateMaxRedeemablePoints = useCallback(async () => {
    if (!selectedOrderForPayment || !paymentCustomer || !pointSettings?.isEnabled) {
      setMaxRedeemablePoints(0);
      setPointsDiscount(0);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/points/max-redeem`, {
        customerId: paymentCustomer._id,
        orderAmount: selectedOrderForPayment.totalAmount,
        hasPromotion: !!selectedOrderForPayment.discount
      });

      if (response.data.success && response.data.data) {
        setMaxRedeemablePoints(response.data.data.maxPoints || 0);
      }
    } catch (error) {
      console.error('Error calculating max points:', error);
    }
  }, [selectedOrderForPayment, paymentCustomer, pointSettings]);

  useEffect(() => {
    calculateMaxRedeemablePoints();
  }, [calculateMaxRedeemablePoints]);

  // Update points discount when usePoints changes
  useEffect(() => {
    if (pointSettings && usePoints > 0) {
      setPointsDiscount(usePoints * pointSettings.pointValue);
    } else {
      setPointsDiscount(0);
    }
  }, [usePoints, pointSettings]);

  // Calculate points to earn
  useEffect(() => {
    if (!selectedOrderForPayment || !pointSettings?.isEnabled) {
      setPointsToEarn(0);
      return;
    }

    const orderAmount = selectedOrderForPayment.totalAmount;
    if (orderAmount < pointSettings.minOrderAmount) {
      setPointsToEarn(0);
      return;
    }

    let points = 0;
    if (pointSettings.accumulationMode === 'fixed' || pointSettings.accumulationMode === 'both') {
      points += pointSettings.pointsPerOrder;
    }
    if (pointSettings.accumulationMode === 'amount' || pointSettings.accumulationMode === 'both') {
      if (pointSettings.amountPerPoint > 0) {
        points += Math.floor(orderAmount / pointSettings.amountPerPoint);
      }
    }
    setPointsToEarn(points);
  }, [selectedOrderForPayment, pointSettings]);

  // Auto-fill amount when switching to cash payment
  useEffect(() => {
    if (paymentMethod === 'cash' && selectedOrderForPayment && !amountReceived) {
      setAmountReceived(selectedOrderForPayment.totalAmount.toString());
    }
  }, [paymentMethod, selectedOrderForPayment]);

  const openPaymentModal = useCallback((order) => {
    setSelectedOrderForPayment(order);
    setPaymentMethod('cash');
    // Auto-fill amount received with order total for cash payment
    setAmountReceived(order.totalAmount.toString());
    setChangeReturned(true);
    setSaveBalanceAmount('');
    setUsePoints(0);
    setPointsDiscount(0);
    
    // Reset bank account to default
    const defaultBank = bankAccounts.find(b => b.isDefault);
    setSelectedBankAccount(defaultBank || bankAccounts[0] || null);
    
    if (order.customer) {
      const existingCustomer = customers.find(c => 
        c._id === order.customer._id || c._id === order.customer
      );
      setPaymentCustomer(existingCustomer || null);
    } else {
      setPaymentCustomer(null);
    }
    
    setPaymentCustomerSearch('');
    setPaymentCustomerResults([]);
    setShowNewCustomerInPayment(false);
    setNewPaymentCustomer({ name: '', phone: '', notes: '' });
    setShowPaymentModal(true);
  }, [customers, bankAccounts]);

  const closePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setSelectedOrderForPayment(null);
  }, []);

  const searchPaymentCustomers = useCallback((search) => {
    setPaymentCustomerSearch(search);
    if (search.length >= 1) {
      const filtered = customers.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.phone && c.phone.includes(search))
      );
      setPaymentCustomerResults(filtered.slice(0, 5));
    } else {
      setPaymentCustomerResults([]);
    }
  }, [customers]);

  const calculatePaymentAmounts = useCallback(() => {
    if (!selectedOrderForPayment) return { change: 0, debt: 0, finalTotal: 0 };
    const total = selectedOrderForPayment.totalAmount - pointsDiscount;
    const received = parseFloat(amountReceived) || 0;
    
    if (paymentMethod === 'debt') {
      return { change: 0, debt: total, finalTotal: total };
    }
    if (paymentMethod === 'transfer') {
      return { change: 0, debt: 0, finalTotal: total };
    }
    // Cash
    if (received >= total) {
      return { change: received - total, debt: 0, finalTotal: total };
    }
    return { change: 0, debt: total - received, finalTotal: total };
  }, [selectedOrderForPayment, amountReceived, paymentMethod, pointsDiscount]);

  const completePayment = useCallback(async () => {
    if (!selectedOrderForPayment) return false;
    
    const { change, debt } = calculatePaymentAmounts();
    
    // Validate customer for debt or points
    if ((debt > 0 || usePoints > 0) && !paymentCustomer && !showNewCustomerInPayment) {
      toast.error('Cần chọn hoặc tạo khách hàng để ghi nợ/dùng điểm');
      return false;
    }
    
    if (showNewCustomerInPayment && !newPaymentCustomer.name.trim()) {
      toast.error('Vui lòng nhập tên khách hàng');
      return false;
    }
    
    // Calculate balance to save
    const balanceToSave = !changeReturned && saveBalanceAmount ? parseFloat(saveBalanceAmount) : 0;
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(
        `${API_URL}/orders/${selectedOrderForPayment._id}/complete-payment`, 
        {
          paymentMethod,
          amountReceived: paymentMethod === 'cash' ? (parseFloat(amountReceived) || 0) : null,
          customerId: paymentCustomer?._id,
          newCustomer: showNewCustomerInPayment ? newPaymentCustomer : null,
          keepChange: balanceToSave > 0,
          saveBalanceAmount: balanceToSave,
          usePoints: usePoints > 0 ? usePoints : 0,
          bankAccount: paymentMethod === 'transfer' && selectedBankAccount ? {
            bankName: selectedBankAccount.bankName,
            accountNumber: selectedBankAccount.accountNumber,
            accountName: selectedBankAccount.accountName
          } : null
        }
      );
      
      let message = `Đã xác nhận thanh toán (${PAYMENT_LABELS[paymentMethod]})`;
      if (pointsDiscount > 0) message += ` - Dùng ${usePoints} điểm: -${formatCurrency(pointsDiscount)}`;
      if (change > 0 && changeReturned) message += ` - Trả lại: ${formatCurrency(change)}`;
      if (balanceToSave > 0) message += ` - Giữ lại: ${formatCurrency(balanceToSave)}`;
      if (debt > 0) message += ` - Ghi nợ: ${formatCurrency(debt)}`;
      
      closePaymentModal();
      
      if (response.data.customer) {
        loadCustomers();
      }
      
      loadOrders();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Xử lý thanh toán thất bại');
      return false;
    } finally {
      setProcessingPayment(false);
    }
  }, [
    selectedOrderForPayment, 
    paymentMethod, 
    amountReceived, 
    paymentCustomer, 
    showNewCustomerInPayment, 
    newPaymentCustomer,
    changeReturned,
    saveBalanceAmount,
    usePoints,
    pointsDiscount,
    selectedBankAccount,
    calculatePaymentAmounts,
    closePaymentModal,
    loadOrders,
    loadCustomers
  ]);

  return {
    showPaymentModal,
    selectedOrderForPayment,
    paymentMethod,
    setPaymentMethod,
    amountReceived,
    setAmountReceived,
    paymentCustomer,
    setPaymentCustomer,
    paymentCustomerSearch,
    paymentCustomerResults,
    showNewCustomerInPayment,
    setShowNewCustomerInPayment,
    newPaymentCustomer,
    setNewPaymentCustomer,
    processingPayment,
    changeReturned,
    setChangeReturned,
    saveBalanceAmount,
    setSaveBalanceAmount,
    // Bank accounts
    bankAccounts,
    selectedBankAccount,
    setSelectedBankAccount,
    // Points
    pointSettings,
    usePoints,
    setUsePoints,
    maxRedeemablePoints,
    pointsDiscount,
    pointsToEarn,
    // Functions
    openPaymentModal,
    closePaymentModal,
    searchPaymentCustomers,
    calculatePaymentAmounts,
    completePayment
  };
};
